import { Icon } from "@iconify/react";
import { useCallback, useEffect, useState } from "react";

import { BotaoPrevia } from "../components/PreviaLink";
import {
  Botao,
  Card,
  Carregando,
  ErroInline,
  Segmented,
  Tag,
  TituloTela,
} from "../components/Sga";
import { api } from "../lib/api";
import { ddmmHora, n } from "../lib/format";
import type { Cliente, Conexao, LinkFormulario } from "../lib/types";

type Filtro = "todos" | "diretos" | "representantes" | "sem_link";

interface Resposta {
  clientes: Cliente[];
}

/**
 * Clientes (CNPJ) e seus links de formulário.
 *
 * A base vem do SGA Gestor e não é editável aqui: o Gestor é o dono do
 * cadastro, e um segundo lugar para digitar razão social viraria duas verdades.
 * O que esta tela faz é o que só existe no Messenger — gerar, rotacionar e
 * revogar o link que identifica a empresa na entrada.
 */
export function Clientes({
  periodo,
  podeEscrever,
}: {
  periodo: string;
  podeEscrever: boolean;
}) {
  const [clientes, setClientes] = useState<Cliente[] | null>(null);
  const [conexoesForm, setConexoesForm] = useState<Conexao[]>([]);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  const carregar = useCallback(
    (termo: string) =>
      api
        .get<Resposta>("/clientes", { busca: termo.trim() || undefined })
        .then((d) => {
          setClientes(d.clientes);
          setErro(null);
        })
        .catch((e) => setErro((e as Error).message))
        .finally(() => setCarregando(false)),
    [],
  );

  useEffect(() => {
    // A busca dispara com atraso: digitar não pode virar uma chamada por tecla.
    const t = window.setTimeout(() => carregar(busca), 300);
    return () => window.clearTimeout(t);
  }, [busca, carregar]);

  useEffect(() => {
    api
      .get<{ conexoes: Conexao[] }>("/conexoes")
      .then((d) =>
        setConexoesForm(
          d.conexoes.filter((c) => c.canal === "form" && c.ativo),
        ),
      )
      .catch(() => setConexoesForm([]));
  }, []);

  const gerarLink = async (cliente: Cliente, rotacionar: boolean) => {
    // Rotacionar mata o link que o cliente já tem. Se ele estiver colado num
    // e-mail antigo ou num favorito, para de funcionar sem aviso — por isso a
    // confirmação, e por isso o texto diz o que acontece, não "tem certeza?".
    if (rotacionar) {
      const ok = window.confirm(
        `Gerar um token novo para ${cliente.razao_social}?\n\n` +
          "O link atual para de funcionar na hora. Quem tiver a URL antiga " +
          'salva vai receber "link não encontrado" até você enviar a nova.',
      );
      if (!ok) return;
    }

    const conexao = conexoesForm[0];
    if (!conexao) {
      setErro(
        "Nenhuma conexão de formulário ativa neste tenant. Crie uma em Conexões antes de gerar links.",
      );
      return;
    }

    setOcupado(cliente.id);
    try {
      await api.post(`/clientes/${cliente.id}/links`, {
        conexao_id: conexao.id,
        rotulo: "Suporte",
        rotacionar,
      });
      await carregar(busca);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(null);
    }
  };

  const revogar = async (cliente: Cliente, link: LinkFormulario) => {
    const ok = window.confirm(
      `Revogar o link de ${cliente.razao_social}?\n\n` +
        "A empresa deixa de conseguir abrir chamado por formulário. " +
        "O histórico de mensagens é preservado.",
    );
    if (!ok) return;

    setOcupado(cliente.id);
    try {
      await api.del(`/clientes/${cliente.id}/links/${link.id}`);
      await carregar(busca);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(null);
    }
  };

  const copiar = async (link: LinkFormulario) => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiado(link.id);
      window.setTimeout(() => setCopiado(null), 1800);
    } catch {
      // Clipboard bloqueado (webview sem permissão): seleciona para o operador
      // copiar à mão em vez de falhar em silêncio.
      window.prompt("Copie o link:", link.url);
    }
  };

  const visiveis = (clientes ?? []).filter((c) => {
    switch (filtro) {
      case "diretos":
        return !c.de_representante;
      case "representantes":
        return c.de_representante;
      case "sem_link":
        return !c.links.some((l) => l.ativo);
      default:
        return true;
    }
  });

  const comLink = (clientes ?? []).filter((c) =>
    c.links.some((l) => l.ativo),
  ).length;
  const deRepresentante = (clientes ?? []).filter(
    (c) => c.de_representante,
  ).length;

  return (
    <div className="tela-fixa">
      <TituloTela
        icone="mdi:domain"
        titulo="Clientes e links"
        contexto={periodo}
        acoes={
          <span className="hint" style={{ marginTop: 0 }}>
            Base sincronizada do SGA Gestor
          </span>
        }
      />

      <Card
        className="card-fixo tela-fixa__corpo"
        titulo="Empresas atendidas"
        acoes={
          <>
            <div className="input-group" style={{ width: 300 }}>
              <input
                className="input"
                placeholder="Buscar razão social, CNPJ ou representante..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
              <span className="addon">
                <Icon icon="mdi:magnify" width={16} />
              </span>
            </div>
            <Segmented
              valor={filtro}
              onChange={setFiltro}
              opcoes={[
                { valor: "todos", rotulo: `Todos (${clientes?.length ?? 0})` },
                {
                  valor: "diretos",
                  rotulo: `Diretos (${(clientes?.length ?? 0) - deRepresentante})`,
                },
                {
                  valor: "representantes",
                  rotulo: `Representantes (${deRepresentante})`,
                },
                {
                  valor: "sem_link",
                  rotulo: `Sem link (${(clientes?.length ?? 0) - comLink})`,
                },
              ]}
            />
          </>
        }
      >
        {erro && (
          <ErroInline mensagem={erro} aoTentar={() => carregar(busca)} />
        )}

        <div className="sga-tabela-scroll card-fixo__grid">
          <table
            className="sga-table sga-table--densa"
            style={{ minWidth: 1120 }}
          >
            <thead>
              <tr>
                <th style={{ width: 260 }}>Empresa</th>
                <th style={{ width: 165 }}>CNPJ</th>
                <th style={{ width: 190 }}>Atendimento</th>
                <th>Link de formulário</th>
                <th style={{ width: 95, textAlign: "right" }}>Submissões</th>
                <th style={{ width: 145 }}>Último acesso</th>
                <th style={{ width: 205 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && !clientes && (
                <tr>
                  <td colSpan={7}>
                    <Carregando />
                  </td>
                </tr>
              )}

              {clientes?.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="sga-vazio">
                      <Icon icon="mdi:domain-off" width={24} />
                      Nenhum cliente sincronizado ainda. O SGA Gestor envia a
                      base por
                      <code style={{ margin: "0 4px" }}>
                        /integracoes/gestor/clientes
                      </code>
                      .
                    </div>
                  </td>
                </tr>
              )}

              {clientes && clientes.length > 0 && visiveis.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="sga-vazio">
                      <Icon icon="mdi:filter-off-outline" width={24} />
                      Nenhuma empresa neste filtro.
                    </div>
                  </td>
                </tr>
              )}

              {visiveis.map((c) => {
                const link = c.links.find((l) => l.ativo);
                return (
                  <tr
                    key={c.id}
                    style={c.ativo ? undefined : { opacity: 0.55 }}
                  >
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.rotulo}</div>
                      {c.nome_fantasia && (
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "var(--sga-fg-3-texto)",
                          }}
                        >
                          {c.razao_social}
                        </div>
                      )}
                      {!c.ativo && <Tag kind="mut">inativo no Gestor</Tag>}
                    </td>

                    <td className="mono">{c.cnpj_formatado}</td>

                    <td>
                      {c.de_representante ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                          title="O chamado vai só para o SGA Desk deste representante"
                        >
                          <Icon icon="mdi:account-tie-outline" width={15} />
                          {c.representante_nome ?? "Representante"}
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Icon icon="mdi:office-building-outline" width={15} />
                          SGA direto
                        </span>
                      )}
                    </td>

                    <td>
                      {link ? (
                        <span
                          className="mono"
                          style={{
                            fontSize: 11.5,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 340,
                          }}
                          title={link.url}
                        >
                          {link.url}
                        </span>
                      ) : (
                        <span data-zero="true">sem link gerado</span>
                      )}
                    </td>

                    <td
                      className="num"
                      data-zero={link?.submissoes ? undefined : "true"}
                    >
                      {n(link?.submissoes ?? 0)}
                    </td>

                    <td
                      className="mono"
                      data-zero={link?.ultimo_acesso_em ? undefined : "true"}
                      style={{ fontSize: 11.5 }}
                    >
                      {link?.ultimo_acesso_em
                        ? ddmmHora(link.ultimo_acesso_em)
                        : "—"}
                    </td>

                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        {link ? (
                          <>
                            <Botao
                              icone={
                                copiado === link.id
                                  ? "mdi:check"
                                  : "mdi:content-copy"
                              }
                              onClick={() => copiar(link)}
                            >
                              {copiado === link.id ? "Copiado" : "Copiar"}
                            </Botao>
                            <BotaoPrevia url={link.url} compacto />
                            <Botao
                              icone="mdi:autorenew"
                              onClick={() => gerarLink(c, true)}
                              disabled={ocupado === c.id || !podeEscrever}
                              title="Gera um token novo. O link atual para de funcionar."
                            />
                            <Botao
                              variante="danger"
                              icone="mdi:link-off"
                              onClick={() => revogar(c, link)}
                              disabled={ocupado === c.id || !podeEscrever}
                              title="Revoga o link. O histórico é preservado."
                            />
                          </>
                        ) : (
                          <Botao
                            variante="primary"
                            icone="mdi:link-plus"
                            onClick={() => gerarLink(c, false)}
                            disabled={
                              ocupado === c.id || !c.ativo || !podeEscrever
                            }
                            title={
                              c.ativo
                                ? undefined
                                : "Cliente inativo no SGA Gestor — reative lá antes de gerar o link."
                            }
                          >
                            Gerar link
                          </Botao>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div
          className="card-fixo__rodape"
          style={{
            display: "flex",
            fontSize: 11.5,
            color: "var(--sga-fg-3)",
            paddingTop: 8,
          }}
        >
          <span>
            {n(visiveis.length)} de {n(clientes?.length ?? 0)} empresas ·{" "}
            {n(comLink)} com link ativo
          </span>
          <span style={{ flex: 1 }} />
          <span>
            {conexoesForm.length === 0
              ? "Nenhuma conexão de formulário ativa"
              : `Formulário: ${conexoesForm[0].nome}`}
          </span>
        </div>
      </Card>
    </div>
  );
}
